// ============================================
// PAGINAPRODUCTO.JS — Vista detalle V2 (Cadillac)
// Renderizada en /tienda/producto/:id_unico (autenticada)
// ============================================
// Trae todo lo que el catálogo expone: galería + iconos visuales +
// caracteristicas + descripción HTML + descargas + empaque + FAQ + stock por
// sucursal + similares + accesorios + sustitutos.
// Compartir WhatsApp directo. Cotizar a cliente vía SelectorCliente.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
// SEGURIDAD-ENDPOINTS-ABIERTOS (2026-09-18): el catalogo ya exige sesion.
import { authHeaders } from './utils/storage';
import ReactDOM from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import {
  API_BASE_CLI, PP, COLOR_OSCURO, COLOR_FONDO, COLOR_GRIS,
  COLOR_GRIS_CLARO, COLOR_BORDE, COLOR_PRINCIPAL,
  estiloBotonPrimario, estiloBotonSecundario,
} from './components/helpersClientes';
import SelectorMoneda, { useMoneda } from './components/SelectorMoneda';
import { useIvaModo, ToggleIva, conIvaSegun } from './components/SelectorIva';
import SelectorCliente, { useClienteTienda } from './components/SelectorCliente';
import CarritoFlotante, { useCarrito, abrirCarrito } from './components/CarritoFlotante';
import BarraAgregandoACotizacion from './components/BarraAgregandoACotizacion';
import { useModoAgregar, leerModoAgregar, salirModoAgregar } from './utils/modoAgregarCotizacion';
import { fetchAuth } from './utils/sesion';
// INVENTARIO = LA TIENDA (2026-10-19): la cara de almacen (existencia/disponible/apartado, lotes, apartar, surtir).
import SeccionAlmacenProducto from './components/almacen/SeccionAlmacenProducto';
import AgregarAListaModal from './components/AgregarAListaModal';
// DUPLICAR-MIS-PRODUCTOS: mismo modal de Mis Productos (CatalogoAdmin), pre-llenado.
import { ModalProducto } from './CatalogoAdmin';
import { useResponsive } from './hooks/useResponsive';
import { optimizarImagen } from './utils/imagenes';
import { hace, masViejoQue } from './utils/tiempo';
import { STORAGE_KEYS, storage } from './utils/storage';

const COLOR_BEEP = '#16a34a';
const COLOR_SYSCOM = '#185FA5';
const COLOR_LIMA = '#94b803';

function colorMargen(pct) {
  if (pct == null) return COLOR_GRIS_CLARO;
  if (pct >= 25) return '#16a34a';
  if (pct >= 15) return '#f59e0b';
  return '#e05c5c';
}

// Limpia el HTML de descripcion que llega CRUDO del proveedor (Syscom). Ese HTML trae basura
// que en movil rompe la experiencia: enlaces/anclas (<a href="#..."> -> al tocar, la pagina
// BRINCA a otro lado), cajas "responsive" de video (div con padding-bottom + height:0 que, al
// quitar el iframe, quedan como HUECOS enormes en blanco), scripts/iframes/estilos peligrosos y
// posicionamientos absolutos que descuadran el layout. Se limpia con el parser real del
// navegador (DOMParser), mucho mas seguro que regex para estructura anidada.
function sanitizeHTML(html) {
  if (!html || typeof html !== 'string') return '';
  // Fallback sin DOM (SSR / entorno de test): limpieza minima por regex.
  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
    return html
      .replace(/<script[\s\S]*?<\/script>/gi, '')
      .replace(/<iframe[\s\S]*?<\/iframe>/gi, '')
      .replace(/<a\b[^>]*>/gi, '').replace(/<\/a>/gi, '')
      .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*')/gi, '')
      .replace(/javascript:/gi, '');
  }
  let doc;
  try { doc = new window.DOMParser().parseFromString(html, 'text/html'); }
  catch { return ''; }
  const body = doc && doc.body;
  if (!body) return '';

  // 1. Fuera elementos peligrosos o pesados (nunca deben renderizarse en la ficha).
  body.querySelectorAll('script, style, link, iframe, video, audio, object, embed, noscript, form, input, textarea, button, svg')
    .forEach(el => el.remove());

  // 2. Cajas "spacer" de video responsive (padding-bottom hack). Si quedaron VACIAS tras quitar
  //    el iframe, se eliminan (eran el hueco en blanco). Si aun tienen contenido real, solo se
  //    neutraliza el hack de alto para que no reserven espacio basura.
  body.querySelectorAll('[style*="padding-bottom"]').forEach(el => {
    // Solo el "hack" de video responsive: padding-bottom en PORCENTAJE + height:0. Asi NO se toca
    // el padding-bottom decorativo en px de titulos/parrafos (h3, etc.) del propio contenido.
    const estilo = el.getAttribute('style') || '';
    const esHackVideo = /padding-bottom\s*:\s*[\d.]+%/i.test(estilo) && /height\s*:\s*0/i.test(estilo);
    if (!esHackVideo) return;
    const tieneContenido = el.querySelector('img') || el.textContent.trim() !== '';
    if (!tieneContenido) { el.remove(); return; }
    el.style.paddingBottom = '';
    el.style.height = '';
  });

  // 3. Enlaces y anclas: se conserva el TEXTO/imagen, se mata la navegacion (causa del brinco al
  //    tocar). <a> -> <span>.
  body.querySelectorAll('a').forEach(a => {
    const span = doc.createElement('span');
    span.innerHTML = a.innerHTML;
    a.replaceWith(span);
  });

  // 4. Atributos: fuera manejadores on*, hrefs/src javascript:, y posicionamiento fixed/absolute
  //    (saca cosas de flujo -> huecos y solapes).
  body.querySelectorAll('*').forEach(el => {
    Array.from(el.attributes).forEach(attr => {
      const nombre = attr.name.toLowerCase();
      if (nombre.startsWith('on')) el.removeAttribute(attr.name);
      if ((nombre === 'href' || nombre === 'src') && /^\s*javascript:/i.test(attr.value)) el.removeAttribute(attr.name);
      // id/name son DESTINOS de anclas internas (href="#destino"). Aunque ya convertimos los <a>
      // en <span>, quitar los destinos garantiza que NINGUN toque pueda brincar la vista dentro
      // de la pagina (defensa en profundidad para el bug de "salta a otra seccion al tocar").
      if (nombre === 'id' || nombre === 'name') el.removeAttribute(attr.name);
    });
    const estilo = (el.getAttribute && el.getAttribute('style')) || '';
    if (/position\s*:\s*(fixed|absolute)/i.test(estilo)) el.style.position = 'static';
  });

  // 5. Imagenes del contenido: carga tolerante y responsivas (que no desborden ni brinquen).
  body.querySelectorAll('img').forEach(img => {
    img.setAttribute('loading', 'lazy');
    img.setAttribute('decoding', 'async');
    img.style.maxWidth = '100%';
    if (!img.style.height) img.style.height = 'auto';
  });
  // 5b. Tablas de specs del proveedor: suelen traer ancho fijo (ej. width:900px) que en movil/
  //     tablet vertical desborda y CORTA columnas. Se vuelven scrollables DENTRO de su caja.
  body.querySelectorAll('table').forEach(t => {
    t.style.display = 'block';
    t.style.overflowX = 'auto';
    t.style.maxWidth = '100%';
    t.style.webkitOverflowScrolling = 'touch';
  });
  // 5c. Cualquier elemento con ancho fijo en px no debe exceder el contenedor (evita bleed de
  //     pagina). Se le pone tope al 100%; el contenido interno se acomoda o hace scroll (tablas).
  body.querySelectorAll('[style*="width"]').forEach(el => {
    const st = el.getAttribute('style') || '';
    if (/(^|[;\s])width\s*:\s*\d+px/i.test(st)) el.style.maxWidth = '100%';
  });

  // 6. Colapsar huecos: elementos totalmente vacios (sin texto ni imagen). Se repite para
  //    arrastrar contenedores que quedaron vacios al vaciarse sus hijos.
  for (let pasada = 0; pasada < 3; pasada++) {
    let quito = false;
    body.querySelectorAll('div, p, section, span').forEach(el => {
      if (el.children.length === 0 && !el.querySelector('img') && el.textContent.trim() === '') {
        el.remove(); quito = true;
      }
    });
    if (!quito) break;
  }

  return body.innerHTML;
}

// `overlay` (opcional) permite montar esta MISMA ficha como capa encima del
// cotizador SIN cambiar de ruta (asi no se desmonta el editor ni se pierde su
// estado). overlay = { idUnico, label, onVolver, onIrAProducto }:
//  - idUnico: sustituye al useParams cuando no hay ruta.
//  - label: texto del boton de retorno ("Volver a la cotizacion COT-XXXX").
//  - onVolver: cierra la capa (en vez de navigate(-1)).
//  - onIrAProducto(idu): abre otro producto DENTRO de la capa (similares/relacionados).
export default function PaginaProducto({ overlay } = {}) {
  const { id_unico: idParam } = useParams();
  const id_unico = overlay?.idUnico ?? idParam;
  const navigate = useNavigate();
  // Navegar a otro producto: dentro de la capa usa el callback (no cambia ruta);
  // en modo pagina normal, navega por ruta como siempre.
  const irAProducto = useCallback((idu) => {
    if (overlay?.onIrAProducto) overlay.onIrAProducto(idu);
    else navigate(`/tienda/producto/${encodeURIComponent(idu)}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlay, navigate]);
  const { formatear } = useMoneda();
  // Switch global Con/Sin IVA (mismo helper que las tarjetas de La Tienda).
  const [ivaModo, setIvaModo] = useIvaModo();
  const [cliente] = useClienteTienda();
  const { agregarItem, carrito } = useCarrito();
  const { isMobile, isTablet } = useResponsive();
  // F3.B: en "modo agregar a cotizacion" se oculta el carrito flotante.
  const modoAgregar = useModoAgregar();
  const totalItemsCarrito = Number(carrito?.total_items || 0);
  const subtotalCarrito = Number(carrito?.subtotal_mxn || 0);

  const [data, setData] = useState(null);
  const [similares, setSimilares] = useState([]);
  const [accesorios, setAccesorios] = useState([]);
  const [sustitutos, setSustitutos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const [imgActiva, setImgActiva] = useState(0);
  const [zoomAbierto, setZoomAbierto] = useState(false);
  const [videoAbierto, setVideoAbierto] = useState(null);
  const [tab, setTab] = useState('specs');
  const [cantidad, setCantidad] = useState(1);
  const [popoverStock, setPopoverStock] = useState(false);
  const [modalLista, setModalLista] = useState(null);
  const [toast, setToast] = useState(null);
  // DUPLICAR-MIS-PRODUCTOS: { producto (prefill o existente), idExistente | null }
  const [modalDuplicar, setModalDuplicar] = useState(null);
  const [guardandoDuplicar, setGuardandoDuplicar] = useState(false);
  const [refrescando, setRefrescando] = useState(false);
  // beep_producto variable: id de la presentacion elegida (selector).
  const [varSelId, setVarSelId] = useState(null);

  // Carga la ficha desde el catalogo local. Ya no acepta un modo "refresh": el
  // unico invocador que lo usaba era el boton "⟳ Refrescar" que se retiro, y
  // actualizar contra Syscom ahora va por handleRefrescar (endpoint dedicado con
  // aviso de que cambio). El parametro ?refresh del backend sigue existiendo para
  // el refresco perezoso interno, pero esta pantalla no lo dispara.
  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    setImgActiva(0);
    try {
      const qs = new URLSearchParams();
      if (cliente?.id) qs.set('cliente_id', String(cliente.id));
      const url = `${API_BASE_CLI}/api/tienda/producto/${encodeURIComponent(id_unico)}?${qs.toString()}`;
      const r = await fetch(url, { headers: authHeaders() }).then(x => x.json());
      if (!r.ok) throw new Error(r.error || 'no encontrado');
      setData(r.producto);
    } catch (e) {
      setError(e.message);
    } finally { setCargando(false); }
  }, [id_unico, cliente?.id]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { window.scrollTo(0, 0); }, [id_unico]);

  // Al cargar un item con variantes (beep_producto o beep_cable), preselecciona la primera.
  useEffect(() => {
    if (data && data.tiene_variantes === true
        && Array.isArray(data.variantes) && data.variantes.length > 0) {
      setVarSelId(data.variantes[0].id);
    }
  }, [data]);

  // Side fetches: similares + accesorios + sustitutos (no bloquean render)
  useEffect(() => {
    if (!data) return;
    const qs = cliente?.id ? `?cliente_id=${cliente.id}` : '';
    const base = `${API_BASE_CLI}/api/tienda/producto/${encodeURIComponent(id_unico)}`;
    fetch(`${base}/similares${qs}`, { headers: authHeaders() }).then(x => x.json()).then(r => r.ok && setSimilares(r.similares || [])).catch(() => {});
    fetch(`${base}/accesorios${qs}`, { headers: authHeaders() }).then(x => x.json()).then(r => r.ok && setAccesorios(r.accesorios || [])).catch(() => {});
    if (Number(data.stock_total ?? data.stock) <= 0) {
      fetch(`${base}/sustitutos${qs}`, { headers: authHeaders() }).then(x => x.json()).then(r => r.ok && setSustitutos(r.sustitutos || [])).catch(() => {});
    } else {
      setSustitutos([]);
    }
  }, [data, id_unico, cliente?.id]);

  // SEO meta
  useEffect(() => {
    if (!data) return;
    document.title = `${data.titulo || data.nombre} | ${data.marca || 'Beep'}`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.content = (data.descripcion_corta || data.titulo || data.nombre || '').slice(0, 160);
  }, [data]);

  const mostrarToast = useCallback((m, duracion = 2200) => { setToast(m); setTimeout(() => setToast(null), duracion); }, []);

  // REFRESCO-SYSCOM: consulta a Syscom SOLO este producto, actualiza el catalogo
  // local y repinta la ficha con lo que llego. A diferencia del viejo "Refrescar"
  // (que fallaba en SILENCIO y dejaba creer que el dato era fresco), aqui se dice
  // exactamente que cambio — o por que no se pudo.
  const handleRefrescar = useCallback(async () => {
    if (refrescando) return;
    setRefrescando(true);
    try {
      const qs = cliente?.id ? `?cliente_id=${encodeURIComponent(cliente.id)}` : '';
      const resp = await fetchAuth(
        `${API_BASE_CLI}/api/tienda/producto/${encodeURIComponent(id_unico)}/refrescar${qs}`,
        { method: 'POST' }
      );
      const d = await resp.json().catch(() => ({}));
      if (!resp.ok || !d.ok) {
        mostrarToast('⚠️ ' + (d.error || 'No se pudo actualizar desde Syscom'), 6000);
        return;
      }
      if (d.producto_completo) setData(d.producto_completo);
      if (d.cambios && d.cambios.length > 0) {
        mostrarToast('✅ Actualizado: ' + d.resumen, 5000);
      } else {
        mostrarToast('✅ ' + (d.resumen || 'Ya estaba al dia'), 3000);
      }
    } catch (e) {
      if (e.code !== 'SESION_EXPIRADA') {
        mostrarToast('⚠️ ' + (e.message || 'No se pudo actualizar desde Syscom'), 5000);
      }
    } finally {
      setRefrescando(false);
    }
  }, [id_unico, cliente?.id, mostrarToast, refrescando]);

  // DUPLICAR-MIS-PRODUCTOS (mismo flujo que en la lista de La Tienda): abre el modal
  // de Mis Productos pre-llenado con lo de Syscom; si ya existe ese SKU, ofrece editarlo.
  const handleDuplicarMisProductos = useCallback(async (p) => {
    const sku = (p.modelo || '').trim();
    try {
      if (sku) {
        const r = await fetch(`${API_BASE_CLI}/api/catalogo/productos/existe?sku=${encodeURIComponent(sku)}`, { headers: authHeaders() }).then(x => x.json());
        if (r.ok && r.data) {
          const pestanaEx = r.data.tipo === 'cable' ? 'Cableado'
            : r.data.tipo === 'servicio' ? 'Servicios e Instalaciones' : 'Productos';
          const editar = window.confirm(
            `Ya lo tienes en Mis Productos (pestaña ${pestanaEx}): "${r.data.nombre}"` +
            `${r.data.stock != null ? ` — inventario actual: ${r.data.stock}` : ''}.\n\n` +
            'Aceptar = editar ese producto (precio / inventario). Cancelar = no hacer nada.'
          );
          if (editar) setModalDuplicar({ producto: r.data, idExistente: r.data.id });
          return;
        }
      }
      const path = Array.isArray(p.categoria_path) ? p.categoria_path : [];
      // FIX-DUPLICAR: derivar tipo (cable -> pestana Cableado), igual que en Tienda.js.
      const textoTipo = `${path.join(' ')} ${p.titulo || p.nombre || ''}`.toLowerCase();
      const esCable = /cable|alambre|bobina|utp|coaxial/.test(textoTipo);
      setModalDuplicar({
        producto: {
          nombre: p.titulo || p.nombre || '',
          descripcion: p.descripcion || '',
          sku,
          proveedor: p.marca || 'Syscom',
          categoria: path.length ? path[path.length - 1] : 'Otros',
          imagen_url: p.imagen_url || p.imagen || null,
          costo_interno_mxn: Number(p.costo_mxn) || 0,
          precio_venta_mxn: Number(p.precio_efectivo_mxn) || 0,
          tipo: esCable ? 'cable' : 'producto',
          unidad: 'pieza',
          moneda_base: 'MXN',
        },
        idExistente: null,
      });
    } catch (e) {
      mostrarToast('⚠️ ' + (e.message || 'No se pudo abrir el duplicado'));
    }
  }, [mostrarToast]);

  const guardarDuplicado = useCallback(async (payload, id) => {
    setGuardandoDuplicar(true);
    try {
      const url = id
        ? `${API_BASE_CLI}/api/catalogo/productos/${id}`
        : `${API_BASE_CLI}/api/catalogo/productos`;
      const resp = await fetchAuth(url, {
        method: id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const d = await resp.json().catch(() => ({}));
      if (!resp.ok || !d.ok) { mostrarToast('⚠️ No se pudo guardar: ' + (d.error || 'error del servidor'), 5000); return; }
      setModalDuplicar(null);
      const pestana = payload.tipo === 'cable' ? 'Cableado'
        : payload.tipo === 'servicio' ? 'Servicios e Instalaciones' : 'Productos';
      mostrarToast(`✅ ${id ? 'Actualizado' : 'Creado'} en Mis Productos → pestaña ${pestana}. Búscalo por su modelo en La Tienda`, 5000);
    } catch (e) {
      if (e.code !== 'SESION_EXPIRADA') mostrarToast('⚠️ ' + (e.message || 'No se pudo guardar'));
    } finally {
      setGuardandoDuplicar(false);
    }
  }, [mostrarToast]);

  // Volver: si hay history en la SPA, retroceder (Tienda rehidratará sus
  // filtros desde sessionStorage y Dashboard restaurará la sección activa
  // desde localStorage). Si entraron directo al link, ir a la home con
  // beep_dashboard_activo apuntando a 'cotizaciones.tienda' para que
  // Dashboard abra La Tienda al montar.
  const volverAtras = useCallback(() => {
    // En modo capa (desde el cotizador): cerrar la capa y volver a la cotizacion
    // EXACTA, sin desmontar el editor (no se pierde scroll ni estado).
    if (overlay?.onVolver) { overlay.onVolver(); return; }
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      storage.set(STORAGE_KEYS.DASHBOARD_ACTIVE_TAB, 'cotizaciones.tienda');
      navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlay, navigate]);

  // Galería: fotos + videos + 360 mezclados como miniaturas
  const galeria = useMemo(() => {
    if (!data) return [];
    const fotos = Array.isArray(data.imagenes) && data.imagenes.length > 0
      ? data.imagenes.map(url => ({ tipo: 'foto', url }))
      : (data.imagen_url ? [{ tipo: 'foto', url: data.imagen_url }] : []);
    const vids = Array.isArray(data.videos) ? data.videos.map(v => ({ tipo: 'video', ...v })) : [];
    const tour = data.tour_360 ? [{ tipo: '360', url: data.tour_360 }] : [];
    return [...fotos, ...tour, ...vids];
  }, [data]);

  if (cargando && !data) return <SkeletonProducto />;

  if (error) return (
    <div style={{ padding: 40, fontFamily: PP, textAlign: 'center' }}>
      <div style={{ fontSize: 36, marginBottom: 8 }}>⚠️</div>
      <div style={{ fontSize: 14, color: COLOR_OSCURO, fontWeight: 700 }}>Producto no encontrado</div>
      <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginTop: 6 }}>{error}</div>
      {/* Usa volverAtras (no navigate directo) para que EN LA CAPA tambien cierre el overlay y
          regrese a la cotizacion exacta. Sin esto, si un producto quedo descontinuado (404) la
          capa se quedaba abierta para siempre y en movil (sin tecla Escape) el usuario quedaba
          atrapado. Muestra el mismo label del banner cuando esta en overlay. */}
      <button style={{ ...estiloBotonSecundario, marginTop: 16 }} onClick={volverAtras}>
        ← {overlay ? overlay.label : 'Volver'}
      </button>
    </div>
  );
  if (!data) return null;

  const esBeep = data.fuente === 'beep';
  const esPactado = !!data.es_pactado;
  const elementoActivo = galeria[imgActiva] || galeria[0] || null;
  const fotoActual = elementoActivo?.tipo === 'foto' ? elementoActivo.url : (galeria.find(g => g.tipo === 'foto')?.url);

  const precioListaUSD = Number(data.precio_lista_usd || 0);
  const precioEspecialUSD = Number(data.precio_especial_usd || 0);
  const precioDescuentoUSD = Number(data.precio_descuento_usd || 0);
  const precioEfectivo = Number(data.precio_efectivo_mxn || 0);
  const precioVenta = Number(data.precio_venta_mxn || 0);
  const muestraTachado = esPactado && precioVenta > precioEfectivo;
  const costoMXN = Number(data.costo_mxn || 0);
  const margen = data.margen_pct != null ? Number(data.margen_pct) : null;
  const stock = Number(data.stock_total ?? data.stock ?? 0);
  const esBeepProducto = data.tipo === 'beep_producto';

  // REFRESCO-SYSCOM: solo productos del proveedor (los propios se editan en Mis
  // Productos). textoActualizado dice de cuando es el dato; en ambar si ya lleva
  // mas de una semana, que es la senal de "esto puede estar desfasado".
  const puedeRefrescar = data.fuente === 'syscom';
  // La tablet tambien se toca con el dedo: minimo 44px igual que en movil.
  const esTactil = isMobile || isTablet;
  const textoActualizado = puedeRefrescar ? hace(data.actualizado_at) : null;
  const datoViejo = puedeRefrescar && masViejoQue(data.actualizado_at, 7);

  // Variantes embebidas (beep_producto o beep_cable): selector + precio segun la elegida.
  const variantesPP = (data.tiene_variantes === true && Array.isArray(data.variantes))
    ? data.variantes : [];
  const tieneVariantesPP = variantesPP.length > 0;
  // Items propios de Beep / con variantes no manejan stock: siempre disponibles.
  const sinStockNoAplica = esBeepProducto || tieneVariantesPP;
  const sinStock = sinStockNoAplica ? false : stock <= 0;
  const vSelPP = tieneVariantesPP
    ? (variantesPP.find(v => String(v.id) === String(varSelId)) || variantesPP[0])
    : null;
  const precioMostrado = tieneVariantesPP ? (Number(vSelPP.precio_venta_mxn) || 0) : precioEfectivo;
  // Sufijo de unidad (ej. "/ metro") cuando no es 'pieza'.
  const sufijoUnidad = (data.unidad && data.unidad !== 'pieza') ? ` / ${data.unidad}` : '';

  const caracteristicas = data.caracteristicas && typeof data.caracteristicas === 'object' && !Array.isArray(data.caracteristicas)
    ? data.caracteristicas
    : {};
  const caracteristicasEntries = Object.entries(caracteristicas);
  const caracteristicasCortas = Array.isArray(data.caracteristicas_cortas) ? data.caracteristicas_cortas : [];
  const docs = Array.isArray(data.documentos) ? data.documentos : [];
  const iconografias = Array.isArray(data.iconos_visuales) && data.iconos_visuales.length > 0
    ? data.iconos_visuales
    : (Array.isArray(data.iconografias) ? data.iconografias : []);
  const stockSucursales = Array.isArray(data.stock_sucursales) ? data.stock_sucursales : [];
  const empaque = data.empaque || null;
  const garantia = data.garantia || null;
  const faq = Array.isArray(data.faq) ? data.faq : [];
  const compatibilidades = Array.isArray(data.compatibilidades) ? data.compatibilidades : [];
  const dims = data.dimensiones || {};
  const tieneLogistica = !!(garantia?.descripcion || data.peso_kg || dims.alto || dims.largo || dims.ancho || data.unidad_medida);

  const onAgregarCarrito = async () => {
    const itemEnviar = tieneVariantesPP
      ? { ...data, referencia_id: `${data.referencia_id}:${vSelPP.id}` }
      : data;
    // F3.C: en "modo agregar a cotizacion" va DIRECTO a la cotizacion (sin carrito), precio
    // de HOY del catalogo. referencia_id ya trae la variante elegida ("base:var").
    const modo = leerModoAgregar();
    if (modo) {
      // Mejora #4b: si el modo es SUSTITUIR -> reemplaza la linea (PUT .../sustituir), sale del
      // modo y vuelve al cotizador. Si no, append normal (F3.C, intacto).
      if (modo.sustituirLineaId != null) {
        try {
          const resp = await fetchAuth(`${API_BASE_CLI}/api/cotizaciones/${modo.cotizacionId}/productos/${modo.sustituirLineaId}/sustituir`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ cliente_id: cliente?.id || null, tipo: itemEnviar.tipo, referencia_id: itemEnviar.referencia_id }),
          });
          const d = await resp.json().catch(() => ({}));
          if (!resp.ok || !d.ok) { mostrarToast('⚠️ ' + (d.error || 'No se pudo sustituir')); return; }
          mostrarToast('✅ Producto sustituido');
          salirModoAgregar();
          try { window.dispatchEvent(new CustomEvent('beep-editar-cotizacion', { detail: { cotizacionId: modo.cotizacionId } })); } catch { /* none */ }
        } catch (e) {
          if (e.code !== 'SESION_EXPIRADA') mostrarToast('⚠️ ' + (e.message || 'No se pudo sustituir'));
        }
        return;
      }
      try {
        const resp = await fetchAuth(`${API_BASE_CLI}/api/cotizaciones/${modo.cotizacionId}/productos`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cliente_id: cliente?.id || null,
            items: [{ tipo: itemEnviar.tipo, referencia_id: itemEnviar.referencia_id, cantidad }],
          }),
        });
        const d = await resp.json().catch(() => ({}));
        if (!resp.ok || !d.ok) { mostrarToast('⚠️ ' + (d.error || 'No se pudo agregar a la cotizacion')); return; }
        mostrarToast(`✅ Agregado a la cotizacion: ${(data.titulo || data.nombre || '').slice(0, 40)}`);
        // F3.F: avisa a la barra para refrescar el contador "Llevas N" al instante.
        try { window.dispatchEvent(new CustomEvent('beep-agregado-a-cotizacion')); } catch { /* none */ }
      } catch (e) {
        if (e.code !== 'SESION_EXPIRADA') mostrarToast('⚠️ ' + (e.message || 'No se pudo agregar'));
      }
      return; // NO sale del modo: se pueden agregar varios seguidos.
    }
    try {
      await agregarItem(itemEnviar, cantidad);
      mostrarToast(`✅ Agregado: ${cantidad} × ${(data.titulo || data.nombre || '').slice(0, 40)}`);
    } catch (e) { mostrarToast('⚠️ ' + e.message); }
  };

  const compartirWhatsApp = () => {
    const url = window.location.href;
    const lineas = [
      'Hola, te comparto este producto:',
      '',
      `*${data.titulo || data.nombre}*`,
      data.marca ? `Marca: ${data.marca}` : '',
      data.modelo ? `Modelo: ${data.modelo}` : '',
      '',
      precioEfectivo > 0 ? `Precio: ${formatear(conIvaSegun(ivaModo, precioEfectivo), 'MXN')} MXN` : '',
      garantia?.descripcion ? `Garantía: ${garantia.descripcion}` : '',
      '',
      `Ver más: ${url}`,
    ].filter(Boolean);
    window.open(`https://wa.me/?text=${encodeURIComponent(lineas.join('\n'))}`, '_blank', 'noopener');
  };

  const tabsDisponibles = [
    caracteristicasEntries.length > 0 && { id: 'specs', label: 'Especificaciones' },
    !!data.descripcion_completa && { id: 'descripcion', label: 'Descripción' },
    compatibilidades.length > 0 && { id: 'compat', label: 'Compatibilidades' },
    !!empaque && (empaque.contenido?.length > 0 || empaque.peso_caja_kg) && { id: 'empaque', label: 'Empaque' },
    faq.length > 0 && { id: 'faq', label: 'FAQ' },
    docs.length > 0 && { id: 'descargas', label: 'Descargas' },
  ].filter(Boolean);
  const tabActivo = tabsDisponibles.find(t => t.id === tab) ? tab : (tabsDisponibles[0]?.id || 'specs');

  const colsCard = isMobile ? 2 : (isTablet ? 3 : 4);

  return (
    <div style={{ fontFamily: PP, color: COLOR_GRIS, background: COLOR_FONDO, minHeight: '100%' }}>
      {/* F3.B: barra "Agregando a la cotizacion X" (se auto-oculta si no hay modo). */}
      <BarraAgregandoACotizacion />
      {/* Header sticky. ANTI-REBOTE (movil): la cabecera es UNA fila de ALTURA FIJA (nowrap +
          height fijo con box-sizing:border-box). Antes, con flexWrap:'wrap' + minHeight, era una
          cabecera de alto VARIABLE: si el ancho del viewport cambiaba un poquito (en Android la
          barra del navegador aparece/desaparece al scrollear y mueve el viewport), la fila podia
          envolver a 2 renglones -> la cabecera crecia de alto -> layout shift -> el scroll cruzaba
          el umbral y rebotaba en bucle (el "resorte de milesimas"). Alto FIJO = jamas cambia = sin
          rebote. En tablet/desktop se conserva el wrap (hay espacio de sobra, no rebota). */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: '#fff', padding: isMobile ? '8px 12px' : '12px 16px',
        borderBottom: `1px solid ${COLOR_BORDE}`,
        display: 'flex', alignItems: 'center', gap: 10, rowGap: 8,
        flexWrap: isMobile ? 'nowrap' : 'wrap',
        boxSizing: 'border-box',
        height: isMobile ? 60 : undefined,
        minHeight: 60,
      }}>
        {/* Volver — siempre visible. La flecha nunca se encoge; el texto (que en modo overlay
            es dinamico: "Volver a la cotizacion <folio/nombre>", puede ser largo) se trunca con
            elipsis y el boton puede encogerse para NO provocar scroll horizontal en movil. */}
        <button
          onClick={volverAtras}
          title={overlay ? overlay.label : undefined}
          style={{
            background: 'transparent', border: 'none',
            color: '#185FA5', fontSize: 14, fontWeight: 600,
            cursor: 'pointer', padding: '8px 10px', borderRadius: 6,
            flexShrink: 1, minWidth: 0, maxWidth: '100%',
            display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: PP,
            minHeight: 44,
            touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
          }}
        >
          <span style={{ flexShrink: 0 }}>←</span>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
            {overlay ? overlay.label : (isMobile ? 'Volver' : 'Volver a La Tienda')}
          </span>
        </button>

        {/* Spacer */}
        <div style={{ flex: 1, minWidth: 8 }} />

        {/* Selector cliente — desktop ≥ 1024 */}
        {!isMobile && !isTablet && (
          <div style={{ minWidth: 180, maxWidth: 240, flexShrink: 1 }}>
            <SelectorCliente compacto />
          </div>
        )}

        {/* Selector IVA + moneda + TC DOF — tablet y desktop. Agrupados y con wrap
            para que en anchos intermedios bajen juntos sin desbordar. */}
        {!isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', flexShrink: 0 }}>
            <ToggleIva modo={ivaModo} onCambio={setIvaModo} compacto={isTablet} />
            <SelectorMoneda compacto={isTablet} />
          </div>
        )}

        {/* WhatsApp — siempre, contraído a icono en mobile/tablet */}
        <button
          onClick={compartirWhatsApp}
          title="Compartir por WhatsApp"
          style={{
            background: '#25D366', color: '#fff', border: 'none',
            padding: (isMobile || isTablet) ? '8px 10px' : '8px 14px',
            borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
            fontFamily: PP, flexShrink: 0,
            display: 'flex', alignItems: 'center', gap: 6,
            whiteSpace: 'nowrap', minHeight: 40,
            touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
          }}
        >
          <span style={{ fontSize: 16 }}>📲</span>
          {!isMobile && !isTablet && <span>WhatsApp</span>}
        </button>

        {/* REFRESCO-SYSCOM: aqui vivia un boton "⟳ Refrescar" de solo escritorio que
            reconsultaba la ficha y, si Syscom fallaba, se quedaba CALLADO mostrando
            el dato viejo como si fuera fresco. Se retiro: la accion "Actualizar desde
            Syscom" ahora esta junto al stock (visible en todos los tamanos), dice que
            cambio y avisa cuando el proveedor rechaza la consulta. */}

        {/* Carrito — F3.F: OCULTO en "modo agregar a cotizacion" (el contador vive en la
            barra ambar). Fuera de modo, EXACTO como hoy. */}
        {!modoAgregar && (
        <button
          onClick={abrirCarrito}
          title="Abrir carrito"
          style={{
            background: '#185FA5', color: '#fff', border: 'none',
            padding: isMobile ? '8px 12px' : '8px 16px',
            borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
            fontFamily: PP, flexShrink: 0,
            display: 'flex', alignItems: 'center', gap: 8,
            whiteSpace: 'nowrap', minHeight: 40,
            boxShadow: '0 2px 4px rgba(24,95,165,0.2)',
            touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
          }}
        >
          <span style={{ fontSize: 16 }}>🛒</span>
          <span>
            {isMobile
              ? (totalItemsCarrito > 0 ? formatear(subtotalCarrito, 'MXN') : 'Carrito')
              : `Carrito · ${formatear(subtotalCarrito, 'MXN')}`}
          </span>
        </button>
        )}
      </div>

      {/* Breadcrumb */}
      {(data.categorias?.length > 0 || (data.categoria_path?.length || 0) > 0) && (
        <div style={{
          padding: '10px 20px', background: '#fff', fontSize: 11, color: COLOR_GRIS_CLARO,
          borderBottom: `1px solid ${COLOR_BORDE}`, overflowX: 'auto', whiteSpace: 'nowrap',
        }}>
          {Array.isArray(data.categorias) && data.categorias.length > 0
            ? data.categorias.map(c => c.nombre).join(' › ')
            : (data.categoria_path || []).join(' › ')}
        </div>
      )}

      <div style={{ padding: isMobile ? 12 : 20, maxWidth: 1280, margin: '0 auto' }}>
        {/* SECCIÓN PRINCIPAL */}
        <div className="pp-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 1fr)',
          gap: isMobile ? 16 : 24, marginBottom: 24,
        }}>
          {/* Galería */}
          <div>
            <div
              onClick={() => fotoActual && setZoomAbierto(true)}
              style={{
                width: '100%', aspectRatio: '1 / 1', maxHeight: 600,
                background: '#fff', borderRadius: 12, border: `1px solid ${COLOR_BORDE}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden', fontSize: 100, color: COLOR_GRIS_CLARO, marginBottom: 12,
                cursor: fotoActual ? 'zoom-in' : 'default',
                position: 'relative',
              }}
            >
              {fotoActual ? (
                <img
                  src={optimizarImagen(fotoActual, 'l')} alt={data.titulo || data.nombre}
                  loading="eager" decoding="async"
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (esBeep ? '🏢' : '📦')}
              {fotoActual && (
                <div style={{
                  position: 'absolute', bottom: 10, right: 10,
                  background: 'rgba(0,0,0,0.55)', color: '#fff', padding: '4px 10px',
                  borderRadius: 99, fontSize: 11, fontWeight: 600,
                }}>🔍 Click para zoom</div>
              )}
            </div>
            {galeria.length > 1 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {galeria.map((g, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      if (g.tipo === 'video') setVideoAbierto(g);
                      else setImgActiva(i);
                    }}
                    style={{
                      width: isMobile ? 64 : 80, height: isMobile ? 64 : 80, borderRadius: 8, padding: 4,
                      border: `2px solid ${i === imgActiva ? COLOR_OSCURO : COLOR_BORDE}`,
                      background: '#fff', cursor: 'pointer', overflow: 'hidden',
                      position: 'relative', flexShrink: 0,
                    }}
                  >
                    {g.tipo === 'foto' ? (
                      <img src={optimizarImagen(g.url, 's')} alt="" loading="lazy" decoding="async"
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div style={{
                        width: '100%', height: '100%', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        fontSize: 22, color: COLOR_PRINCIPAL,
                        background: '#f1f5f9',
                      }}>{g.tipo === '360' ? '🔄' : '▶'}</div>
                    )}
                    {g.tipo === 'video' && (
                      <div style={{
                        position: 'absolute', top: 2, right: 2,
                        background: '#185FA5', color: '#fff',
                        fontSize: 9, padding: '1px 4px', borderRadius: 3, fontWeight: 700,
                      }}>VIDEO</div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            {(esPactado || data.es_nuevo || data.es_mas_vendido || data.es_oferta || data.es_recomendado) && (
              <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
                {esPactado && <BadgeChip color="#f59e0b" texto="🔒 PACTADO" />}
                {data.es_nuevo && <BadgeChip color="#0ea5e9" texto="NUEVO" />}
                {data.es_mas_vendido && <BadgeChip color="#dc2626" texto="MÁS VENDIDO" />}
                {data.es_oferta && <BadgeChip color="#ea580c" texto="OFERTA" />}
                {data.es_recomendado && <BadgeChip color={COLOR_LIMA} texto="RECOMENDADO" />}
              </div>
            )}

            {data.marca && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
              }}>
                {data.marca_logo && (
                  <img
                    src={data.marca_logo} alt={data.marca}
                    loading="lazy"
                    style={{ height: 28, maxWidth: 120, objectFit: 'contain' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                <span style={{
                  fontSize: 12, fontWeight: 700, color: COLOR_LIMA,
                  textTransform: 'uppercase', letterSpacing: 0.6,
                }}>{data.marca}</span>
              </div>
            )}
            <h1 style={{
              fontSize: isMobile ? 18 : 22, fontWeight: 700, color: COLOR_OSCURO,
              lineHeight: 1.3, margin: '0 0 12px',
            }}>{data.titulo || data.nombre}</h1>

            {esBeepProducto && data.descripcion && (
              <div style={{ fontSize: 14, lineHeight: 1.6, color: COLOR_GRIS, marginBottom: 14, whiteSpace: 'pre-line' }}>
                {data.descripcion}
              </div>
            )}

            <div style={{ display: 'grid', gap: 4, fontSize: 13, marginBottom: 14 }}>
              {(data.modelo || data.sku) && (
                <div>
                  <span style={{ color: COLOR_GRIS_CLARO }}>Modelo: </span>
                  <strong>{data.modelo || data.sku}</strong>
                  <button
                    onClick={() => navigator.clipboard?.writeText(data.modelo || data.sku).then(() => mostrarToast('Modelo copiado'))}
                    title="Copiar modelo"
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', marginLeft: 6 }}
                  >📋</button>
                </div>
              )}
              {data.fuente === 'syscom' && data.id_externo && (
                <div><span style={{ color: COLOR_GRIS_CLARO }}>SKU: </span><strong>{data.id_externo}</strong></div>
              )}
              {garantia?.descripcion && (
                <div>
                  <span style={{ color: COLOR_GRIS_CLARO }}>Garantía: </span>
                  <strong>{garantia.descripcion}{garantia.anos ? ` (${garantia.anos} año${garantia.anos === 1 ? '' : 's'})` : ''}</strong>
                </div>
              )}
            </div>

            {/* Precios */}
            <div style={{
              background: '#fff', borderRadius: 12, padding: 18, border: `1px solid ${COLOR_BORDE}`,
              marginBottom: 14,
            }}>
              {muestraTachado && (
                <div style={{ fontSize: 13, color: COLOR_GRIS_CLARO, textDecoration: 'line-through', marginBottom: 4 }}>
                  Normal: {formatear(conIvaSegun(ivaModo, precioVenta), 'MXN')}
                </div>
              )}
              <div style={{ fontSize: isMobile ? 26 : 32, fontWeight: 700, color: COLOR_OSCURO, lineHeight: 1.1 }}>
                {formatear(conIvaSegun(ivaModo, precioMostrado), 'MXN')}
                {sufijoUnidad && (
                  <span style={{ fontSize: isMobile ? 14 : 16, fontWeight: 600, color: COLOR_GRIS_CLARO }}>{sufijoUnidad}</span>
                )}
              </div>
              {costoMXN > 0 && data.fuente === 'syscom' && (
                <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginTop: 6 }}>
                  Costo: {formatear(conIvaSegun(ivaModo, costoMXN), 'MXN')}
                  {margen != null && (
                    <span style={{ marginLeft: 10, color: colorMargen(margen), fontWeight: 700 }}>
                      Margen {margen.toFixed(0)}%
                    </span>
                  )}
                </div>
              )}
              {data.fuente === 'syscom' && (precioListaUSD > 0 || precioEspecialUSD > 0 || precioDescuentoUSD > 0) && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${COLOR_BORDE}`, fontSize: 12 }}>
                  <div style={{ color: COLOR_GRIS_CLARO, marginBottom: 4, fontWeight: 600 }}>Precios mayoreo (USD):</div>
                  {precioListaUSD > 0 && <div>Lista: ${precioListaUSD.toFixed(2)}</div>}
                  {precioEspecialUSD > 0 && <div>Especial: ${precioEspecialUSD.toFixed(2)}</div>}
                  {precioDescuentoUSD > 0 && (
                    <div>Descuento: <strong>${precioDescuentoUSD.toFixed(2)}</strong> <span style={{ color: COLOR_GRIS_CLARO }}>← tu costo</span></div>
                  )}
                  {data.tc_aplicado && (
                    <div style={{ marginTop: 4, color: COLOR_GRIS_CLARO }}>TC aplicado: ${Number(data.tc_aplicado).toFixed(4)}</div>
                  )}
                </div>
              )}
            </div>

            {/* Inventario PROPIO (Mis Productos): avisa pero NUNCA bloquea agregar. */}
            {esBeepProducto && data.stock != null && (
              <div style={{
                fontSize: 14, fontWeight: 600, marginBottom: 14,
                color: Number(data.stock) <= 0 ? '#f59e0b' : (Number(data.stock) > 10 ? '#16a34a' : '#f59e0b'),
              }}>
                ● {Number(data.stock) <= 0
                  ? 'Sin inventario registrado — puedes agregarlo igual'
                  : `Inventario propio: ${data.stock} unidades`}
              </div>
            )}

            {/* Stock + sucursales (no aplica a items propios de Beep con variantes) */}
            {!sinStockNoAplica && (
            <div style={{ position: 'relative', marginBottom: 14 }}>
              <div style={{
                fontSize: 14, fontWeight: 600,
                color: sinStock ? '#e05c5c' : (stock > 10 ? '#16a34a' : '#f59e0b'),
                display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
              }}>
                <span>● {sinStock ? 'Sin stock' : `Disponible: ${stock} unidades`}</span>
                {/* DESCONTINUADOS-SYSCOM: Syscom dejo de listar este producto (existencia 0). */}
                {data.descontinuado && (
                  <span style={{ color: COLOR_GRIS_CLARO, fontWeight: 500, fontSize: 12 }}>
                    Descontinuado por proveedor
                  </span>
                )}
                {data.tiempo_entrega && !sinStock && (
                  <span style={{ color: COLOR_GRIS_CLARO, fontWeight: 500, fontSize: 12 }}>
                    🚚 {data.tiempo_entrega}
                  </span>
                )}
                {stockSucursales.length > 0 && (
                  <button
                    onClick={() => setPopoverStock(s => !s)}
                    style={{
                      background: 'transparent', border: `1px solid ${COLOR_BORDE}`,
                      borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer',
                      fontFamily: PP, color: COLOR_GRIS, fontWeight: 600,
                      minHeight: esTactil ? 44 : 30, touchAction: 'manipulation',
                    }}
                  >Ver por sucursal ▾</button>
                )}

                {/* REFRESCO-SYSCOM: consulta a Syscom SOLO este producto y refresca
                    existencias/precio/datos. Junto al stock, que es donde nace la duda. */}
                {puedeRefrescar && (
                  <button
                    type="button"
                    onClick={handleRefrescar}
                    disabled={refrescando}
                    aria-busy={refrescando}
                    aria-label="Actualizar desde Syscom"
                    title="Consultar existencias y precio actuales en Syscom"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      background: 'transparent', border: `1px solid ${COLOR_BORDE}`,
                      borderRadius: 6, padding: '4px 10px', fontSize: 11,
                      cursor: refrescando ? 'wait' : 'pointer',
                      fontFamily: PP, color: refrescando ? COLOR_GRIS_CLARO : COLOR_SYSCOM,
                      fontWeight: 700, minHeight: esTactil ? 44 : 30,
                      touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
                    }}
                  >
                    <RefreshCw
                      size={12}
                      strokeWidth={2.5}
                      style={refrescando ? { animation: 'spin 0.9s linear infinite' } : undefined}
                    />
                    {refrescando ? 'Consultando Syscom…' : 'Actualizar desde Syscom'}
                  </button>
                )}
              </div>

              {/* Antiguedad del dato: en ambar si pasa de una semana. */}
              {puedeRefrescar && textoActualizado && (
                <div style={{
                  marginTop: 6, fontSize: 11, fontWeight: 600,
                  color: datoViejo ? '#f59e0b' : COLOR_GRIS_CLARO,
                }}>
                  Datos actualizados {textoActualizado}
                </div>
              )}
              {popoverStock && stockSucursales.length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, marginTop: 6, zIndex: 30,
                  background: '#fff', border: `1px solid ${COLOR_BORDE}`, borderRadius: 8,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: 10, minWidth: 240,
                }}>
                  <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, fontWeight: 700, marginBottom: 6, textTransform: 'uppercase' }}>
                    Stock por sucursal
                  </div>
                  {stockSucursales.map((s, i) => (
                    <div key={i} style={{
                      display: 'flex', justifyContent: 'space-between', padding: '6px 0',
                      borderBottom: i < stockSucursales.length - 1 ? `1px solid ${COLOR_BORDE}` : 'none',
                      fontSize: 13,
                    }}>
                      <span>{s.sucursal}</span>
                      <strong style={{ color: s.stock > 0 ? '#16a34a' : '#e05c5c' }}>{s.stock}</strong>
                    </div>
                  ))}
                </div>
              )}
            </div>
            )}

            {/* Selector de presentacion (beep_producto variable) */}
            {tieneVariantesPP && (
              <div style={{ marginBottom: 14 }}>
                <div style={{
                  fontSize: 11, color: COLOR_GRIS_CLARO, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6,
                }}>Presentacion</div>
                <select
                  value={String(varSelId ?? '')}
                  onChange={(e) => setVarSelId(e.target.value)}
                  aria-label="Elegir presentacion"
                  style={{
                    width: '100%', maxWidth: 340, minHeight: 44, padding: '10px 12px',
                    fontSize: 14, fontFamily: PP, color: COLOR_OSCURO, background: '#fff',
                    border: `1px solid ${COLOR_BORDE}`, borderRadius: 8, cursor: 'pointer',
                  }}
                >
                  {variantesPP.map(v => (
                    <option key={v.id} value={String(v.id)}>{v.etiqueta}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Cantidad + acciones. En modo overlay (ficha abierta DESDE una cotizacion) la
                vista es SOLO-LECTURA: se ocultan carrito, agregar/sustituir y lista pactada para
                no modificar ninguna cotizacion desde aqui (regla del sprint COTIZADOR-FICHA). */}
            {!overlay && (<>
            <div style={{ display: 'inline-flex', alignItems: 'center', border: `1px solid ${COLOR_BORDE}`, borderRadius: 8, marginBottom: 12, background: '#fff' }}>
              <button style={btnQty} onClick={() => setCantidad(c => Math.max(1, c - 1))}>−</button>
              <input
                type="number" min="1" value={cantidad}
                onChange={(e) => setCantidad(Math.max(1, Number(e.target.value) || 1))}
                style={{ width: 60, textAlign: 'center', border: 'none', outline: 'none', fontFamily: PP, fontSize: 16, fontWeight: 600, background: 'transparent' }}
              />
              <button style={btnQty} onClick={() => setCantidad(c => c + 1)}>+</button>
            </div>

            <div style={{ display: 'grid', gap: 8 }}>
              <button
                onClick={onAgregarCarrito}
                disabled={sinStock}
                style={{
                  ...estiloBotonPrimario, padding: '14px 18px', fontSize: 15,
                  opacity: sinStock ? 0.5 : 1, cursor: sinStock ? 'not-allowed' : 'pointer',
                }}
              >🛒 {modoAgregar ? 'Agregar a la cotización' : 'Agregar al carrito'}</button>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <button
                  onClick={() => setModalLista({
                    nombre_display: data.titulo || data.nombre,
                    tipo: data.tipo,
                    referencia_id: data.referencia_id,
                    precio_base_mxn: data.costo_mxn || data.precio_efectivo_mxn,
                  })}
                  style={{ ...estiloBotonPrimario, background: COLOR_BEEP }}
                >📋 Lista pactada</button>
                <button
                  onClick={compartirWhatsApp}
                  style={{ ...estiloBotonPrimario, background: '#25D366' }}
                >📲 WhatsApp</button>
              </div>
              {/* DUPLICAR-MIS-PRODUCTOS: solo en fichas Syscom. El caso tipico es un
                  producto sin stock Syscom que Ricardo SI tiene: se duplica con su
                  inventario propio y ya se puede cotizar. */}
              {data.fuente === 'syscom' && (
                <button
                  onClick={() => handleDuplicarMisProductos(data)}
                  style={{ ...estiloBotonSecundario, padding: '12px 18px', minHeight: 44 }}
                >⧉ Duplicar a Mis Productos{sinStock ? ' (yo sí lo tengo)' : ''}</button>
              )}
            </div>
            </>)}
          </div>
        </div>

        {/* Iconos visuales */}
        {iconografias.length > 0 && (
          <BarraIconografias iconografias={iconografias} />
        )}

        {/* Características principales (lista corta) */}
        {caracteristicasCortas.length > 0 && (
          <CaracteristicasBullets items={caracteristicasCortas} isMobile={isMobile} />
        )}

        {/* INVENTARIO = LA TIENDA: en el almacen (se oculta sola si no hay permiso de Almacen) */}
        <SeccionAlmacenProducto idUnico={id_unico} isMobile={isMobile} />

        {/* Info logística */}
        {tieneLogistica && (
          <InfoLogistica
            garantia={garantia}
            peso={data.peso_kg}
            dims={dims}
            unidad={data.unidad_medida}
            isMobile={isMobile}
          />
        )}

        {/* Tabs */}
        {tabsDisponibles.length > 0 && (
          <div style={{ background: '#fff', borderRadius: 12, padding: isMobile ? 14 : 20, border: `1px solid ${COLOR_BORDE}`, marginBottom: 24 }}>
            <div style={{
              display: 'flex', gap: 4, marginBottom: 16, borderBottom: `1px solid ${COLOR_BORDE}`,
              overflowX: 'auto', WebkitOverflowScrolling: 'touch',
            }}>
              {tabsDisponibles.map(t => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  style={{
                    padding: '10px 16px', fontSize: 13, fontFamily: PP, cursor: 'pointer',
                    background: 'transparent', border: 'none',
                    color: tabActivo === t.id ? COLOR_OSCURO : COLOR_GRIS_CLARO,
                    fontWeight: tabActivo === t.id ? 700 : 500,
                    borderBottom: tabActivo === t.id ? `3px solid ${COLOR_OSCURO}` : '3px solid transparent',
                    marginBottom: -1, whiteSpace: 'nowrap', flexShrink: 0,
                  }}
                >{t.label}</button>
              ))}
            </div>

            {tabActivo === 'specs' && <TabSpecs entries={caracteristicasEntries} />}
            {tabActivo === 'descripcion' && <TabDescripcion html={data.descripcion_completa} />}
            {tabActivo === 'compat' && <TabCompatibilidades items={compatibilidades} onIr={(idu) => irAProducto(idu)} />}
            {tabActivo === 'empaque' && <TabEmpaque empaque={empaque} peso={data.peso_kg} dims={data.dimensiones} />}
            {tabActivo === 'faq' && <TabFAQ faq={faq} />}
            {tabActivo === 'descargas' && <TabDescargas docs={docs} />}
          </div>
        )}

        {/* Sustitutos (solo si sin stock) */}
        {sustitutos.length > 0 && sinStock && (
          <SeccionProductos titulo="Productos sustitutos disponibles" productos={sustitutos} cols={colsCard} onIr={irAProducto} destacado />
        )}

        {/* Similares */}
        {similares.length > 0 && (
          <SeccionProductos titulo="Productos similares" productos={similares} cols={colsCard} onIr={irAProducto} />
        )}

        {/* Accesorios */}
        {accesorios.length > 0 && (
          <SeccionProductos titulo="Accesorios recomendados" productos={accesorios} cols={colsCard} onIr={irAProducto} />
        )}
      </div>

      {/* F3.B: carrito oculto en modo agregar a cotizacion. */}
      {!modoAgregar && <CarritoFlotante triggerOculto />}

      {modalLista && (
        <AgregarAListaModal
          item={modalLista}
          onCerrar={() => setModalLista(null)}
          onGuardado={() => { setModalLista(null); mostrarToast('✅ Agregado a lista pactada'); }}
        />
      )}

      {/* DUPLICAR-MIS-PRODUCTOS: mismo modal de CatalogoAdmin, pre-llenado. */}
      {modalDuplicar && (
        <ModalProducto
          producto={modalDuplicar.producto}
          onGuardar={(payload) => guardarDuplicado(payload, modalDuplicar.idExistente)}
          onCerrar={() => setModalDuplicar(null)}
          guardando={guardandoDuplicar}
        />
      )}

      {zoomAbierto && fotoActual && (
        <ModalZoom imagen={fotoActual} alt={data.titulo || data.nombre} onCerrar={() => setZoomAbierto(false)} />
      )}
      {videoAbierto && (
        <ModalVideo video={videoAbierto} onCerrar={() => setVideoAbierto(null)} />
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)',
          background: '#1a3a5c', color: '#fff', padding: '10px 16px', borderRadius: 10,
          fontSize: 12, zIndex: 1500, fontFamily: PP,
        }}>{toast}</div>
      )}

      <style>{`
        @media (max-width: 880px) {
          .pp-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

// ============================================
// SUB-COMPONENTES
// ============================================

function BadgeChip({ color, texto }) {
  return (
    <span style={{
      padding: '4px 10px', borderRadius: 99, fontSize: 10, fontWeight: 700,
      background: color, color: '#fff', letterSpacing: 0.5,
    }}>{texto}</span>
  );
}

function BarraIconografias({ iconografias }) {
  // El catálogo entrega los iconos como imágenes "completas" (composición visual
  // que ya incluye texto). No mostramos ni el nombre ni el tooltip si no vienen,
  // y dejamos los íconos lo más grandes posible.
  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: 14, border: `1px solid ${COLOR_BORDE}`,
      marginBottom: 16,
      display: 'flex', gap: 16, alignItems: 'center',
      // ANTI-SALTO (todos los tamanos): los iconos van SIEMPRE en UNA sola fila con scroll
      // horizontal (nowrap), nunca envuelven. Antes, con 'wrap', al cargar cada icono ganaba
      // ancho y podia empujar filas nuevas -> la barra CRECIA de alto y en Safari/WebKit
      // (iOS, iPadOS y macOS, que NO tienen scroll anchoring) el contenido de abajo saltaba y
      // el scroll "regresaba". Una fila que nunca envuelve tiene altura FIJA (minHeight) desde
      // el primer render, asi que estructuralmente no puede provocar el salto en NINGUN ancho
      // (celular, tablet vertical/horizontal, PC). flex-start para poder alcanzar el primer
      // icono al desplazar (justify-content:center escondería el inicio cuando hay overflow).
      flexWrap: 'nowrap',
      justifyContent: 'flex-start',
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch',
      minHeight: 60,
    }}>
      {iconografias.filter(ic => ic.imagen).map((ic, i) => (
        <img
          key={i}
          src={ic.imagen}
          alt={ic.nombre || 'Característica'}
          title={ic.tooltip || ic.nombre || ''}
          loading="lazy"
          style={{
            height: 60, maxWidth: 160, objectFit: 'contain', flexShrink: 0,
          }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      ))}
    </div>
  );
}

function CaracteristicasBullets({ items, isMobile }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: isMobile ? 16 : 20,
      border: `1px solid ${COLOR_BORDE}`, marginBottom: 16,
    }}>
      <h3 style={{
        fontSize: 12, fontWeight: 700, color: COLOR_GRIS_CLARO,
        textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 12px',
      }}>Características principales</h3>
      <ul style={{
        listStyle: 'none', padding: 0, margin: 0,
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: 8,
      }}>
        {items.map((c, i) => (
          <li key={i} style={{
            display: 'flex', gap: 8, alignItems: 'flex-start',
            fontSize: 14, color: COLOR_OSCURO, lineHeight: 1.4,
          }}>
            <span style={{ color: COLOR_LIMA, fontWeight: 700, flexShrink: 0 }}>›</span>
            <span style={{ flex: 1 }}>{c}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function InfoLogistica({ garantia, peso, dims, unidad, isMobile }) {
  const items = [];
  if (garantia?.descripcion) {
    const txt = garantia.descripcion + (garantia.anos ? ` (${garantia.anos} año${garantia.anos === 1 ? '' : 's'})` : '');
    items.push({ label: 'Garantía', valor: txt });
  } else if (typeof garantia === 'string' && garantia) {
    items.push({ label: 'Garantía', valor: garantia });
  }
  if (peso && Number(peso) > 0) {
    items.push({ label: 'Peso', valor: `${peso} kg` });
  }
  if (dims && (dims.largo || dims.ancho || dims.alto)) {
    items.push({
      label: 'Dimensiones',
      valor: `${dims.largo || '—'} × ${dims.ancho || '—'} × ${dims.alto || '—'} cm`,
    });
  }
  if (unidad) items.push({ label: 'Unidad', valor: unidad });

  if (items.length === 0) return null;

  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: 16,
      border: `1px solid ${COLOR_BORDE}`, marginBottom: 16,
      display: 'grid',
      gridTemplateColumns: isMobile
        ? `repeat(${Math.min(items.length, 2)}, 1fr)`
        : `repeat(${Math.min(items.length, 4)}, 1fr)`,
      gap: 16,
    }}>
      {items.map((it, i) => (
        <div key={i}>
          <div style={{
            fontSize: 11, color: COLOR_GRIS_CLARO, textTransform: 'uppercase',
            letterSpacing: 0.5, fontWeight: 700,
          }}>{it.label}</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: COLOR_OSCURO, marginTop: 2 }}>
            {it.valor}
          </div>
        </div>
      ))}
    </div>
  );
}

function TabSpecs({ entries }) {
  if (entries.length === 0) {
    return <div style={{ color: COLOR_GRIS_CLARO, fontStyle: 'italic', fontSize: 13 }}>Sin especificaciones disponibles.</div>;
  }
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <tbody>
        {entries.map(([k, v], i) => (
          <tr key={i} style={{ background: i % 2 ? '#f8fafc' : '#fff' }}>
            <td style={{ padding: '10px 12px', color: COLOR_GRIS_CLARO, fontWeight: 600, width: '38%', verticalAlign: 'top' }}>{k}</td>
            <td style={{ padding: '10px 12px', color: COLOR_OSCURO }}>{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Escapa una cadena para usarla dentro de un RegExp (los src traen ., /, ?, etc.)
function escaparRegex(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function TabDescripcion({ html }) {
  const limpio = useMemo(() => sanitizeHTML(html), [html]);
  // ANTI-SALTO iOS: las imagenes de la descripcion (banners/diagramas de Syscom) NO traen
  // ancho/alto, asi que al cargar crecen de 0 a su tamano real y EMPUJAN el contenido. En
  // iOS Safari (sin scroll anchoring) eso hace que la pagina "salte" mientras se lee. Solucion:
  // PRE-CARGAR cada imagen para conocer su proporcion real, inyectar `aspect-ratio` (que
  // reserva el alto exacto ANTES de pintar) y recien entonces mostrar la descripcion. Cero
  // salto y sin cambiar como se ve (la proporcion es la real de cada imagen).
  const [htmlListo, setHtmlListo] = useState(null);
  useEffect(() => {
    if (!limpio) { setHtmlListo(''); return; }
    const srcs = [...limpio.matchAll(/<img[^>]+src=['"]([^'"]+)['"]/gi)].map(m => m[1]);
    if (srcs.length === 0) { setHtmlListo(limpio); return; }
    let cancelado = false;
    const unicos = [...new Set(srcs)];
    Promise.all(unicos.map(src => new Promise(res => {
      const im = new Image();
      im.onload = () => res({ src, w: im.naturalWidth, h: im.naturalHeight });
      im.onerror = () => res({ src, w: 0, h: 0 });
      im.src = src;
    }))).then(dims => {
      if (cancelado) return;
      let out = limpio;
      dims.forEach(({ src, w, h }) => {
        if (!w || !h) return;
        // Flag 'g': si la MISMA imagen aparece varias veces en la descripcion, todas reservan
        // su alto (sin 'g' solo la primera quedaba fija y una repetida podia seguir saltando).
        const re = new RegExp(`(<img[^>]*src=['"]${escaparRegex(src)}['"][^>]*?)(/?>)`, 'gi');
        out = out.replace(re, (m, apertura, cierre) => {
          const reserva = `max-width:100%;height:auto;aspect-ratio:${w} / ${h};`;
          if (/style\s*=/i.test(apertura)) {
            return apertura.replace(/style\s*=\s*(['"])([\s\S]*?)\1/i,
              (mm, q, st) => `style=${q}${st};${reserva}${q}`) + cierre;
          }
          return `${apertura} style="${reserva}"${cierre}`;
        });
      });
      setHtmlListo(out);
    });
    return () => { cancelado = true; };
  }, [limpio]);

  if (!limpio) return <div style={{ color: COLOR_GRIS_CLARO, fontStyle: 'italic', fontSize: 13 }}>Sin descripción.</div>;
  if (htmlListo === null) {
    // Mientras se pre-cargan las imagenes: placeholder breve (evita mostrar y luego saltar).
    // Estilo inline autocontenido: la clase .skeleton vive en el <style> de SkeletonProducto,
    // que ya esta desmontado aqui, asi que NO se puede depender de ella (se veria caja vacia).
    return <div style={{ height: 240, borderRadius: 12, background: COLOR_FONDO, border: `1px solid ${COLOR_BORDE}` }} />;
  }
  return (
    <div
      // Defensa en profundidad: si algun <a>/ancla del proveedor sobreviviera al saneo, este
      // interceptor mata el toque ANTES de que el navegador brinque la pagina a un #destino.
      onClickCapture={(e) => {
        const a = e.target && e.target.closest && e.target.closest('a');
        if (a) { e.preventDefault(); }
      }}
      // Red de seguridad: si algo del contenido crudo del proveedor sigue siendo mas ancho que la
      // tarjeta (tabla, texto sin espacios, etc.), hace scroll DENTRO de su caja en vez de
      // desbordar la pagina. overflowWrap parte cadenas larguisimas (URLs) para no romper el ancho.
      style={{ fontSize: 14, lineHeight: 1.7, color: COLOR_OSCURO, maxWidth: '100%', overflowX: 'auto', overflowWrap: 'break-word' }}
      dangerouslySetInnerHTML={{ __html: htmlListo }}
    />
  );
}

function TabCompatibilidades({ items, onIr }) {
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {items.map((c, i) => (
        <button
          key={i}
          onClick={() => onIr(c.id_unico)}
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '10px 14px', border: `1px solid ${COLOR_BORDE}`, borderRadius: 8,
            background: '#fff', cursor: 'pointer', fontFamily: PP, textAlign: 'left',
          }}
        >
          <span style={{ fontWeight: 600, color: COLOR_OSCURO, fontSize: 13 }}>{c.titulo}</span>
          <span style={{ fontSize: 11, color: COLOR_GRIS_CLARO }}>{c.razon} →</span>
        </button>
      ))}
    </div>
  );
}

function TabEmpaque({ empaque, peso, dims }) {
  const contenido = empaque?.contenido || [];
  const pesoCaja = empaque?.peso_caja_kg || peso;
  const d = empaque?.dimensiones_caja || dims || {};
  return (
    <div style={{ fontSize: 13, lineHeight: 1.7 }}>
      {contenido.length > 0 && (
        <>
          <div style={{ fontWeight: 700, color: COLOR_OSCURO, marginBottom: 6 }}>¿Qué viene en la caja?</div>
          <ul style={{ margin: '0 0 14px 18px' }}>
            {contenido.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
        {pesoCaja && <SpecRow label="Peso" value={`${pesoCaja} kg`} />}
        {(d.largo || d.ancho || d.alto) && (
          <SpecRow label="Dimensiones" value={`${d.largo || '—'} × ${d.ancho || '—'} × ${d.alto || '—'} cm`} />
        )}
        {empaque?.cantidad_caja && <SpecRow label="Por caja" value={`${empaque.cantidad_caja} pza`} />}
      </div>
    </div>
  );
}

function TabFAQ({ faq }) {
  const [abiertos, setAbiertos] = useState(new Set());
  const toggle = (i) => {
    setAbiertos(prev => {
      const n = new Set(prev);
      if (n.has(i)) n.delete(i); else n.add(i);
      return n;
    });
  };
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {faq.map((q, i) => (
        <div key={i} style={{ border: `1px solid ${COLOR_BORDE}`, borderRadius: 8, overflow: 'hidden' }}>
          <button
            onClick={() => toggle(i)}
            style={{
              width: '100%', textAlign: 'left', padding: '12px 14px',
              background: '#fff', border: 'none', cursor: 'pointer', fontFamily: PP,
              fontSize: 13, fontWeight: 600, color: COLOR_OSCURO,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
            }}
          >
            <span style={{ flex: 1 }}>{q.pregunta}</span>
            <span>{abiertos.has(i) ? '−' : '+'}</span>
          </button>
          {abiertos.has(i) && (
            <div style={{
              padding: '10px 14px', background: '#f8fafc', fontSize: 13, color: COLOR_GRIS,
              borderTop: `1px solid ${COLOR_BORDE}`,
            }}>{q.respuesta}</div>
          )}
        </div>
      ))}
    </div>
  );
}

function TabDescargas({ docs }) {
  if (docs.length === 0) {
    return <div style={{ color: COLOR_GRIS_CLARO, fontStyle: 'italic', fontSize: 13 }}>Sin descargas disponibles.</div>;
  }
  const iconoTipo = (t) => ({
    datasheet: '📄', manual: '📘', driver: '⚙️', certificado: '🏅', otro: '📎',
  }[t] || '📎');
  const badgeExt = (ext) => {
    const e = (ext || '').toUpperCase();
    if (!e) return null;
    const color = e === 'PDF' ? '#dc2626' : (['EXE', 'ZIP', 'MSI'].includes(e) ? '#185FA5' : '#64748b');
    return (
      <span style={{
        background: color, color: '#fff', padding: '3px 7px',
        borderRadius: 4, fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
      }}>{e}</span>
    );
  };
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {docs.map((d, i) => (
        <a key={i} href={d.url} target="_blank" rel="noopener noreferrer"
          style={{
            padding: 12, border: `1px solid ${COLOR_BORDE}`, borderRadius: 8,
            textDecoration: 'none', color: COLOR_OSCURO, fontWeight: 600,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            background: '#fff', fontSize: 13, fontFamily: PP, gap: 10,
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: 20 }}>{iconoTipo(d.tipo)}</span>
            {badgeExt(d.extension)}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.nombre}</span>
          </span>
          <span style={{ color: COLOR_GRIS_CLARO, fontSize: 11, flexShrink: 0 }}>
            Descargar ↓
          </span>
        </a>
      ))}
    </div>
  );
}

function SeccionProductos({ titulo, productos, cols, destacado, onIr }) {
  const { formatear } = useMoneda();
  // Mismo switch global Con/Sin IVA que el resto de la vista.
  const [ivaModo] = useIvaModo();
  return (
    <div style={{ marginBottom: 28 }}>
      <h2 style={{
        fontSize: 16, fontWeight: 700,
        color: destacado ? '#dc2626' : COLOR_OSCURO,
        margin: '0 0 14px',
      }}>{titulo}</h2>
      <div style={{
        display: 'grid', gap: 12,
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
      }}>
        {productos.map(rel => (
          <button
            key={rel.id_unico}
            onClick={() => onIr(rel.id_unico)}
            style={{
              background: '#fff', border: `1px solid ${COLOR_BORDE}`, borderRadius: 10,
              padding: 12, textAlign: 'left', cursor: 'pointer', fontFamily: PP,
              transition: 'box-shadow 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
          >
            <div style={{
              width: '100%', aspectRatio: '1 / 1', background: COLOR_FONDO,
              borderRadius: 8, marginBottom: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden', fontSize: 32,
            }}>
              {rel.imagen_url ? (
                <img src={optimizarImagen(rel.imagen_url, 'm')} alt="" loading="lazy" decoding="async"
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : '📦'}
            </div>
            {rel.marca && (
              <div style={{ fontSize: 9, fontWeight: 700, color: COLOR_GRIS_CLARO, textTransform: 'uppercase' }}>{rel.marca}</div>
            )}
            <div style={{
              fontSize: 12, fontWeight: 600, color: COLOR_OSCURO, lineHeight: 1.3,
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
              minHeight: 30,
            }}>{rel.nombre || rel.titulo}</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_OSCURO, marginTop: 6 }}>
              {formatear(conIvaSegun(ivaModo, rel.precio_efectivo_mxn || 0), 'MXN')}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function ModalZoom({ imagen, alt, onCerrar }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCerrar(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onCerrar]);
  const contenido = (
    <div onClick={onCerrar} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 99999,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      cursor: 'zoom-out',
    }}>
      <img src={optimizarImagen(imagen, 'l')} alt={alt}
        style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 8 }}
      />
      <button onClick={(e) => { e.stopPropagation(); onCerrar(); }}
        style={{
          position: 'absolute', top: 16, right: 16, fontSize: 28, color: '#fff',
          background: 'rgba(0,0,0,0.4)', border: 'none', borderRadius: '50%',
          width: 44, height: 44, cursor: 'pointer',
        }}
      >×</button>
    </div>
  );
  return ReactDOM.createPortal(contenido, document.body);
}

function ModalVideo({ video, onCerrar }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCerrar(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onCerrar]);

  const embed = useMemo(() => {
    if (video.tipo === 'youtube') {
      const m = video.url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]+)/);
      const id = m ? m[1] : null;
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    if (video.tipo === 'vimeo') {
      const m = video.url.match(/vimeo\.com\/(\d+)/);
      return m ? `https://player.vimeo.com/video/${m[1]}` : null;
    }
    return null;
  }, [video]);

  const contenido = (
    <div onClick={onCerrar} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 99999,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: '90vw', maxWidth: 900, aspectRatio: '16 / 9',
        background: '#000', borderRadius: 8, overflow: 'hidden', position: 'relative',
      }}>
        {embed ? (
          <iframe
            src={embed} title={video.titulo || 'Video'}
            allow="autoplay; encrypted-media; fullscreen" allowFullScreen
            style={{ width: '100%', height: '100%', border: 'none' }}
          />
        ) : (
          <video src={video.url} controls autoPlay
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        )}
      </div>
      <button onClick={(e) => { e.stopPropagation(); onCerrar(); }}
        style={{
          position: 'absolute', top: 16, right: 16, fontSize: 28, color: '#fff',
          background: 'rgba(0,0,0,0.4)', border: 'none', borderRadius: '50%',
          width: 44, height: 44, cursor: 'pointer',
        }}
      >×</button>
    </div>
  );
  return ReactDOM.createPortal(contenido, document.body);
}

function SkeletonProducto() {
  return (
    <div style={{ padding: 20, fontFamily: PP, maxWidth: 1280, margin: '0 auto' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 24 }}>
        <div className="skeleton" style={{ aspectRatio: '1 / 1', borderRadius: 12 }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div className="skeleton" style={{ height: 14, width: '40%', borderRadius: 4 }} />
          <div className="skeleton" style={{ height: 24, borderRadius: 4 }} />
          <div className="skeleton" style={{ height: 24, width: '70%', borderRadius: 4 }} />
          <div className="skeleton" style={{ height: 80, borderRadius: 12, marginTop: 8 }} />
          <div className="skeleton" style={{ height: 44, borderRadius: 8, marginTop: 12 }} />
        </div>
      </div>
      <style>{`
        .skeleton {
          background: linear-gradient(90deg, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%);
          background-size: 200% 100%;
          animation: ppShimmer 1.4s infinite;
        }
        @keyframes ppShimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
}

function SpecRow({ label, value }) {
  if (!value) return null;
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', gap: 16,
      padding: '8px 0', borderBottom: `1px solid ${COLOR_BORDE}`,
    }}>
      <span style={{ color: COLOR_GRIS_CLARO }}>{label}</span>
      <span style={{ color: COLOR_GRIS, fontWeight: 600, textAlign: 'right' }}>{value}</span>
    </div>
  );
}

const btnQty = {
  background: 'transparent', border: 'none', padding: '10px 14px',
  fontSize: 18, fontWeight: 700, cursor: 'pointer', color: COLOR_GRIS,
};
